import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useState } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Edit,
  FormDataConsumer,
  FormTab,
  Labeled,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  minValue,
  useNotify,
  useTranslate
} from 'react-admin';
import { Link, useNavigate } from 'react-router-dom';
import { PriceInput, Tooltip, Wrapper } from '../../components';
import Permission from '../../components/Permission';
import { RichTextInputCustom } from '../../components/RichTextInputCustom';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { post } from '../../lib/request';
import { TagType } from '../../models';
import { APP_CONFIG_RESOURCE } from '../appConfig/tabs';
import { FirebaseMissingAlert, FirebaseOnly, orderCards } from './firebaseConfig';

export default () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();

  const SgaTriggerButton = () => {
    const [loading, setLoading] = useState(false);

    const handleTriggerSga = async () => {
      setLoading(true);
      try {
        await post(`${DEFAULT_API_ADMIN}/chains/sga/trigger-job`, {});
        notify('Job SGA iniciado com sucesso!', { type: 'success' });
      } catch (error) {
        notify('Erro ao iniciar job SGA', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleTriggerSga}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
        sx={{ mt: 2, mb: 2 }}
      >
        {loading ? 'Executando...' : 'Executar Job SGA Agora'}
      </Button>
    );
  };

  const onSuccess = () => {
    notify('ra.notification.updated', { messageArgs: { smart_count: 1 } });
    navigate('/settings');
  }

  const onError = () => {
    notify('ra.notification.data_provider_error', { type: 'error' });
    navigate('/settings');
  }

  return (
    <Permission permission={PERMISSIONS.SETTINGS}>
      {
        getChainId() ?
          <Edit
            id={getChainId()}
            resource="chains-config"
            title="Configurações"
            mutationMode='pessimistic'
            mutationOptions={{ onSuccess, onError }}
            transform={(props) => {
              return orderCards(props);
            }}
          >
            {/* <TabbedForm toolbar={<CustomToolbar />}> */}
            <TabbedForm>
              <FormTab label="Geral">
                <ReferenceInput
                  source="defaultStationId"
                  reference="stations"
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={9999}
                >
                  <SelectInput
                    label="Posto padrão para vínculo de pontos"
                    optionText="name"
                    style={{ minWidth: 290 }}
                  />
                </ReferenceInput>
                <NumberInput
                  source="pointsExpirationTime"
                  label="Tempo para expiração de pontos (dias)"
                  style={{ minWidth: 290 }}
                  min={0}
                />
                <NumberInput
                  source="couponExpirationDays"
                  label="Tempo para expiração de cupons (dias)"
                  style={{ minWidth: 290 }}
                  min={0}
                />
                <PriceInput source="cashbackFactor" label="Valor do cashback para 1 ponto" precision={3} style={{ minWidth: 290 }} />
                <TextInput
                  source="mex10Token"
                  label="Token Mex10"
                  style={{ minWidth: 290 }}
                />
                <Labeled label="Regras de abastecimento">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
                      <BooleanInput label="Pontuar quando usar desconto" source="pointsWhenDiscounting" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.usePendingTransaction')}>
                      <BooleanInput label="Solicitar aprovação para abastecimento manual/API" source="usePendingTransaction" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Regras de cupons e ofertas">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useChronologyCouponInvoice')}>
                      <BooleanInput label="Ativar cronologia dos cupons" source="useChronologyCouponInvoice" />
                    </Tooltip>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useOnlyRedeemAfterFillin')}>
                      <BooleanInput label="Resgate somente após um abastecimento" source="useOnlyRedeemAfterFillin" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Cadastro de clientes">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
                      <BooleanInput label="Cadastrar senha automática para o cliente(6 primeiros dígitos do CPF)" source="useGeneratedDefaultPassword" />
                    </Tooltip>
                  </>
                </Labeled>
                <Labeled label="Relatórios">
                  <>
                    <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useDateConcentrator')}>
                      <BooleanInput label="Usar data do concentrador nos relatórios" source="useDateConcentrator" />
                    </Tooltip>
                  </>
                </Labeled>
              </FormTab>
              <FormTab label="Regulamento">
                <RichTextInputCustom source="regulation" label="Regulamento" />
              </FormTab>
              <FormTab label="Indicação">
                <FirebaseMissingAlert />
                <NumberInput
                  source="referralValue"
                  label="Valor para quem indica"
                  min={0}
                  style={{ width: 300 }}
                />
                <NumberInput
                  source="refereeValue"
                  label="Valor para indicado"
                  min={0}
                  style={{ width: 300 }}
                />
                <FirebaseOnly>
                  <>
                    <Typography fullWidth>Crie o seu texto de indicação. A palavra "referral_code" será substituída pelo código do usuário.</Typography>
                    <TextInput
                      label="Texto de Indicação"
                      source="firebaseConfig.referral_message"
                      fullWidth
                      multiline
                      defaultValue={"Ganhe desconto ao abastecer! Use o meu código referral_code e ganhe ainda mais pontos ao utilizar o app. Garanta a qualidade e quantidade do combustível e troque pontos por produtos e serviços."} />
                  </>
                </FirebaseOnly>
              </FormTab>
              {/* Aba antiga mantida só com o aviso: é aqui que o operador procura as opções do app. */}
              <FormTab label="Aplicativo">
                <Alert severity="info" sx={{ mb: 2 }}>
                  As opções do aplicativo (Home, carrossel, abastecimento, cupons, avaliação e login) agora ficam em{' '}
                  <Link to={`/${APP_CONFIG_RESOURCE}`}>Aplicativo</Link>.
                </Alert>
              </FormTab>
              <FormTab label="Integrações">
                <FormDataConsumer>
                  {({ formData }) => (
                    formData.useSgaIntegration &&
                    <>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Usuário de acesso à API SGA (Hinova)">
                        <TextInput
                          source="sgaUsername"
                          label="Usuário SGA"
                          style={{ minWidth: 300 }}
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Senha de acesso à API SGA (Hinova)">
                        <TextInput
                          source="sgaPassword"
                          label="Senha SGA"
                          style={{ minWidth: 300 }}
                          type="password"
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Token base de autenticação da API SGA (Hinova)">
                        <TextInput
                          source="sgaApiToken"
                          label="Token API SGA"
                          style={{ minWidth: 400 }}
                          type="password"
                        />
                      </Tooltip>
                      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title={translate('tooltips.defaultTagSGA')}>
                        <ReferenceInput
                          source="defaultTagId"
                          reference="chains/chainId/tag"
                          sort={{ field: 'name', order: 'ASC' }}
                          filter={{ type: TagType.client }}
                          // @ts-ignore
                          perPage={null}
                        >
                          <SelectInput
                            style={{ minWidth: 300 }}
                            optionText="name"
                            label="Etiqueta padrão seguradora SGA"
                            emptyText="Nenhum" />
                        </ReferenceInput>
                      </Tooltip>
                      <Permission permission={PERMISSIONS.ADMIN}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Execução Manual
                          </Typography>
                          <SgaTriggerButton />
                        </Box>
                      </Permission>
                    </>
                  )}
                </FormDataConsumer>
              </FormTab>
              <FormTab label="Métricas de Combustível">
                <ArrayInput
                  source="fuelIntegrationRules"
                  label="Combustíveis"
                >
                  <SimpleFormIterator disableRemove disableReordering disableAdd>
                    <FormDataConsumer>
                      {({ scopedFormData }) => {
                        return (
                          <>
                            <Accordion style={{ width: '100%', marginBottom: 12 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <ReferenceField label="Combustível" link={false} record={scopedFormData} source="fuelId" reference='fuels'>
                                  <TextField source="name" style={{ fontSize: '16px' }} />
                                </ReferenceField>
                              </AccordionSummary>
                              <AccordionDetails>
                                <NumberInput
                                  source="amountToFillingCompletely"
                                  record={scopedFormData}
                                  label="Considerar tanque cheio (L)"
                                  style={{ width: 280 }}
                                  validate={[minValue(0)]}
                                />
                              </AccordionDetails>
                            </Accordion>
                          </>
                        );
                      }}
                    </FormDataConsumer>
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            </TabbedForm>
          </Edit>
          :
          <Wrapper>
            <Typography>Selecione uma rede para fazer alterações</Typography>
          </Wrapper>
      }
    </Permission>
  );
};
